import { ChevronRight } from 'lucide-react'
import styled, { keyframes } from 'styled-components'

export const timelineItemEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(0.375rem);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const incomingGlyphFade = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`

export const FadeInMount = styled.span`
  display: inline-flex;
  animation: ${timelineItemEnter} 220ms ease-out both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

export const StreamingTextRoot = styled.span`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`

export const FadingGlyph = styled.span`
  animation: ${incomingGlyphFade} 240ms ease-out both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

export const ChatTurnRoot = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  gap: 0.75rem;
  animation: ${timelineItemEnter} 180ms ease-out both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

export const ChatBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
`

export const ChatResponseText = styled.div`
  margin: 0;
  min-width: 0;
  max-width: 100%;
  font-size: 0.875rem;
  line-height: 1.375rem;
`

/** Bubble chrome for user chat messages — keep styled for the bordered surface. */
export const UserMessageBubble = styled.div`
  align-self: flex-end;
  width: fit-content;
  max-width: calc(100% - 2rem);
  margin-left: 2rem;
  padding: 0.75rem;
  border: 0.0625rem solid var(--border);
  border-radius: 0.5rem;
  background: var(--muted);
  color: var(--foreground);
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.25rem;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`

export const ThoughtsToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  max-width: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  overflow: visible;
  background: transparent;
  color: var(--muted-foreground);
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.25rem;
  text-align: left;
  cursor: pointer;

  &:hover:not(:disabled) {
    color: var(--foreground);
  }

  &:disabled {
    cursor: default;
  }

  &:focus-visible {
    outline: 0.125rem solid var(--ring);
    outline-offset: 0.125rem;
  }
`

export const ThoughtsChevron = styled(ChevronRight)<{ $open: boolean }>`
  flex-shrink: 0;
  margin-top: 0.125rem;
  transform: rotate(${(props) => (props.$open ? 90 : 0)}deg);
  transition: transform 150ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const ThinkingGif = styled.img`
  display: block;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  object-fit: contain;
`

export const ThoughtsSummary = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
  white-space: normal;
`

export const ThoughtsExpanded = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 0.75rem;
  padding-left: 1.25rem;
`

const detailsReveal = keyframes`
  from {
    opacity: 0;
    transform: translateY(-0.25rem);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`

/** Open body of a `details` response segment: secondary, muted Markdown. */
export const ResponseDetailsBody = styled.div`
  /* AgenticMarkdown paints text with --foreground; mute the whole block. */
  --foreground: var(--muted-foreground);

  margin-top: 0.5rem;
  padding-left: 0.75rem;
  border-left: 0.0625rem solid var(--border);
  color: var(--muted-foreground);
  font-size: 0.8125rem;
  line-height: 1.25rem;
  animation: ${detailsReveal} 180ms ease-out both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

/** Lets a long follow-up question wrap inside its button in a narrow column. */
export const ExpandedThoughtRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0.375rem;
  min-width: 0;
`

export const ExpandedThoughtIconColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: 0.75rem;
`

export const ExpandedThoughtIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  height: 1.25rem;
  color: var(--muted-foreground);
`

export const ExpandedThoughtConnector = styled.span`
  flex: 1 1 auto;
  width: 0.0625rem;
  min-height: 0.75rem;
  margin: 0.125rem 0;
  background: var(--border);
`

export const ExpandedThoughtText = styled.p<{ $isLast?: boolean }>`
  margin: 0;
  min-width: 0;
  padding-bottom: ${(props) => (props.$isLast ? 0 : '0.625rem')};
  color: var(--muted-foreground);
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1.25rem;
  overflow-wrap: anywhere;
`

export const ResultCount = styled.span`
  color: ${(props) => props.theme.colors.success['400_base']};
  animation: ${timelineItemEnter} 180ms ease-out both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

/** Bordered surface for interactive cards inside a chat turn. */
export const InlineTurnCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 0.0625rem solid var(--border);
  border-radius: 0.5rem;
  background: var(--card);
`

export const FallbackNotice = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
  padding: 0.75rem;
  border: 0.0625rem solid var(--border);
  border-radius: 0.5rem;
  background: color-mix(in oklch, var(--muted) 80%, transparent);
`
